

// Contactus.jsx - Complete Updated Version with API Integration
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaFacebook,
  FaInstagram,
  FaTwitter,
  FaLinkedin,
  FaPaperPlane,
  FaUser,
  FaPhoneAlt,
  FaComment,
  FaWhatsapp,
  FaClock,
  FaCheckCircle,
  FaArrowRight,
  FaLeaf,
  FaGem,
  FaStar,
  FaUserCircle,
  FaCamera,
  FaUpload,
  FaShieldAlt,
  FaTrophy,
  FaUsers,
  FaRegClock,
  FaSpinner,
  FaTimesCircle,
  FaExclamationTriangle
} from "react-icons/fa";
import "../css/Contactus.css";

// API Base URL - Update this with your backend URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api';

const Contactus = () => {
  // Contact Form State
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    contactNumber: "",
    email: "",
    comment: "",
  });

  // Review Form State
  const [reviewData, setReviewData] = useState({
    customerName: "",
    customerEmail: "",
    rating: 0,
    reviewText: "",
    productName: "",
    image: null
  });

  // UI States
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitMessageType, setSubmitMessageType] = useState(""); // 'success' | 'error'
  const [reviewSubmitMessage, setReviewSubmitMessage] = useState("");
  const [reviewSubmitMessageType, setReviewSubmitMessageType] = useState("");
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [reviewStats, setReviewStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Load review statistics on component mount
  useEffect(() => {
    fetchReviewStats();
  }, []);

  // Fetch review statistics
  const fetchReviewStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/reviews/stats`);
      const data = await response.json();
      if (data.success) {
        setReviewStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching review stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  // Scroll animation effect
  useEffect(() => {
    const handleScroll = () => {
      const cards = document.querySelectorAll('.info-card-premium');
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          card.style.opacity = '1';
          card.style.transform = 'translateY(0)';
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle contact form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle review form input changes
  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setReviewData({ ...reviewData, [name]: value });
  };

  // Handle rating click
  const handleRatingClick = (rating) => {
    setReviewData({ ...reviewData, rating });
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setReviewSubmitMessageType('error');
        setReviewSubmitMessage("⚠️ File size too large. Please upload an image under 5MB.");
        setTimeout(() => {
          setReviewSubmitMessage("");
          setReviewSubmitMessageType("");
        }, 4000);
        return;
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setReviewSubmitMessageType('error');
        setReviewSubmitMessage("⚠️ Invalid file type. Please upload JPEG, PNG, JPG, or WEBP images.");
        setTimeout(() => {
          setReviewSubmitMessage("");
          setReviewSubmitMessageType("");
        }, 4000);
        return;
      }

      setReviewData({ ...reviewData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle contact form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage("");
    setSubmitMessageType("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitMessageType('success');
        setSubmitMessage(data.message || "✨ Thank you for reaching out! Our team will connect with you shortly. ✨");
        setFormData({
          firstName: "",
          lastName: "",
          contactNumber: "",
          email: "",
          comment: "",
        });
      } else {
        setSubmitMessageType('error');
        setSubmitMessage(data.message || "❌ Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessageType('error');
      setSubmitMessage("❌ Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setSubmitMessage("");
        setSubmitMessageType("");
      }, 6000);
    }
  };

  // Handle review form submission
const handleReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (reviewData.rating === 0) {
        setReviewSubmitMessageType('error');
        setReviewSubmitMessage("⚠️ Please select a rating before submitting your review.");
        setTimeout(() => {
            setReviewSubmitMessage("");
            setReviewSubmitMessageType("");
        }, 4000);
        return;
    }
    
    setIsReviewSubmitting(true);
    setReviewSubmitMessage("");
    setReviewSubmitMessageType("");

    try {
        const formDataToSend = new FormData();
        formDataToSend.append('customerName', reviewData.customerName);
        formDataToSend.append('customerEmail', reviewData.customerEmail);
        formDataToSend.append('rating', reviewData.rating);
        formDataToSend.append('reviewText', reviewData.reviewText);
        formDataToSend.append('productName', reviewData.productName || '');
        if (reviewData.image) {
            formDataToSend.append('image', reviewData.image);
        }

        const response = await fetch(`${API_BASE_URL}/reviews`, {
            method: 'POST',
            body: formDataToSend, // Don't set Content-Type header, browser will set it with boundary
        });

        const data = await response.json();

        if (response.ok && data.success) {
            setReviewSubmitMessageType('success');
            setReviewSubmitMessage(data.message || "⭐ Thank you for your valuable feedback!");
            setReviewData({
                customerName: "",
                customerEmail: "",
                rating: 0,
                reviewText: "",
                productName: "",
                image: null
            });
            setImagePreview(null);
            fetchReviewStats();
        } else {
            setReviewSubmitMessageType('error');
            setReviewSubmitMessage(data.message || "❌ Failed to submit review. Please try again.");
        }
    } catch (error) {
        console.error('Review submission error:', error);
        setReviewSubmitMessageType('error');
        setReviewSubmitMessage("❌ Network error. Please check your connection.");
    } finally {
        setIsReviewSubmitting(false);
        setTimeout(() => {
            setReviewSubmitMessage("");
            setReviewSubmitMessageType("");
        }, 6000);
    }
};

  // Contact information
  const contactInfo = [
    {
      icon: <FaPhone />,
      title: "Phone Number",
      details: ["+91-8180090266"],
      action: "Call Now",
      link: "tel:+918180090266",
      color: "#2E7D32"
    },
    {
      icon: <FaWhatsapp />,
      title: "WhatsApp",
      details: ["+91-8180090266", "Available 9 AM - 7 PM"],
      action: "Chat Now",
      link: "https://wa.me/918180090266",
      color: "#25D366"
    },
    {
      icon: <FaEnvelope />,
      title: "Email Address",
      details: ["dashrath7865p@gmail.com"],
      action: "Send Email",
      link: "mailto:dashrath7865p@gmail.com",
      color: "#EA4335"
    },
    {
      icon: <FaMapMarkerAlt />,
      title: "Visit Our Facility",
      details: ["A-54 MIDC Karad", "Tal-Karad, Dist-Satara", "4115110"],
      action: "Get Directions",
      link: "#",
      color: "#FF6B35"
    }
  ];

  // Social media links
  const socialLinks = [
    { icon: <FaFacebook />, name: "Facebook", color: "#1877f2", url: "https://facebook.com" },
    { icon: <FaInstagram />, name: "Instagram", color: "#E4405F", url: "https://instagram.com" },
    { icon: <FaTwitter />, name: "Twitter", color: "#1DA1F2", url: "https://twitter.com" },
    { icon: <FaLinkedin />, name: "LinkedIn", color: "#0077B5", url: "https://linkedin.com" }
  ];

  // Render star rating
  const renderStars = (rating, interactive = false) => {
    const stars = [];
    const maxRating = 5;
    
    for (let i = 1; i <= maxRating; i++) {
      stars.push(
        <FaStar
          key={i}
          className={`review-star ${i <= (interactive ? hoverRating || rating : rating) ? 'active' : ''}`}
          onClick={() => interactive && handleRatingClick(i)}
          onMouseEnter={() => interactive && setHoverRating(i)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          style={{ 
            cursor: interactive ? 'pointer' : 'default',
            fontSize: interactive ? '2rem' : '1rem',
            color: i <= (interactive ? hoverRating || rating : rating) ? '#FFD700' : '#e2e8f0',
            transition: 'all 0.2s ease'
          }}
        />
      );
    }
    return stars;
  };

  // Render rating labels
  const getRatingLabel = (rating) => {
    const labels = {
      0: 'Tap a star to rate',
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent! ⭐'
    };
    return labels[rating] || labels[0];
  };

  return (
    <div className="contact-page-premium">
      {/* Animated Background */}
      <div className="contact-bg-pattern">
        <div className="bg-circle circle-1"></div>
        <div className="bg-circle circle-2"></div>
        <div className="bg-circle circle-3"></div>
        <div className="bg-leaf leaf-1">🌿</div>
        <div className="bg-leaf leaf-2">🍃</div>
        <div className="bg-leaf leaf-3">🌱</div>
      </div>

      {/* Hero Section */}
      <section className="contact-hero-premium">
        <div className="contact-hero-overlay-premium"></div>
        <div className="contact-hero-content-premium">
          <motion.div
            className="hero-badge-premium"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FaGem className="badge-gem" /> Get in Touch
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Let's Connect &<br />
            <span className="gradient-text-hero">Grow Together</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Have questions about our products or want to partner with us? 
            Reach out and our team will get back to you within 24 hours.
          </motion.p>
          
          <motion.div
            className="hero-stats-premium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="hero-stat">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Support</span>
            </div>
            <div className="stat-divider-hero"></div>
            <div className="hero-stat">
              <span className="stat-number">100%</span>
              <span className="stat-label">Response Rate</span>
            </div>
            <div className="stat-divider-hero"></div>
            <div className="hero-stat">
              <span className="stat-number">24hrs</span>
              <span className="stat-label">Reply Time</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact & Form Section */}
      <section className="contact-main-premium">
        <div className="container-premium">
          <div className="section-header-premium">
            <motion.span 
              className="section-badge"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <FaShieldAlt /> Premium Support
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              We'd Love to <span className="gradient-text-green">Hear</span> From <span className="gradient-text-gold">You</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Choose your preferred way to connect with us
            </motion.p>
          </div>

          <div className="contact-grid-premium">
            {/* Left Side - Info Cards */}
            <motion.div 
              className="contact-info-premium"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="info-cards-grid">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    className="info-card-premium"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -8 }}
                  >
                    <div className="card-icon-premium" style={{ background: item.color }}>
                      {item.icon}
                    </div>
                    <div className="card-content-premium">
                      <h4>{item.title}</h4>
                      {item.details.map((detail, i) => (
                        <p key={i}>{detail}</p>
                      ))}
                      <a href={item.link} className="card-link-premium">
                        {item.action} <FaArrowRight />
                      </a>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Working Hours */}
              <motion.div 
                className="working-hours-premium"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="hours-icon-wrapper">
                  <FaRegClock className="clock-icon" />
                </div>
                <div className="hours-content">
                  <h4>Working Hours</h4>
                  <p><span>Monday - Saturday:</span> 9:00 AM - 7:00 PM</p>
                  <p><span>Sunday:</span> Closed</p>
                </div>
              </motion.div>
            </motion.div>

            {/* Right Side - Contact Form */}
            <motion.div 
              className="contact-form-premium"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="form-wrapper-premium">
                <div className="form-header-premium">
                  <h3>Send Us a <span>Message</span></h3>
                  <p>Fill out the form below and we'll get back to you</p>
                </div>

                {submitMessage && (
                  <div className={`message-banner ${submitMessageType}`}>
                    {submitMessageType === 'success' ? (
                      <FaCheckCircle className="message-icon" />
                    ) : submitMessageType === 'error' ? (
                      <FaTimesCircle className="message-icon" />
                    ) : (
                      <FaExclamationTriangle className="message-icon" />
                    )}
                    <span>{submitMessage}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="contact-form-premium-ui">
                  <div className="form-row-premium">
                    <div className="form-group-premium">
                      <label>
                        <FaUser className="input-icon-premium" />
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="Enter your first name"
                        className="input-premium"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group-premium">
                      <label>
                        <FaUser className="input-icon-premium" />
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Enter your last name"
                        className="input-premium"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-row-premium">
                    <div className="form-group-premium">
                      <label>
                        <FaPhoneAlt className="input-icon-premium" />
                        Contact Number *
                      </label>
                      <input
                        type="tel"
                        name="contactNumber"
                        value={formData.contactNumber}
                        onChange={handleChange}
                        required
                        placeholder="+91 1234567890"
                        className="input-premium"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="form-group-premium">
                      <label>
                        <FaEnvelope className="input-icon-premium" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className="input-premium"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-group-premium full-width-premium">
                    <label>
                      <FaComment className="input-icon-premium" />
                      Your Message *
                    </label>
                    <textarea
                      name="comment"
                      rows="5"
                      value={formData.comment}
                      onChange={handleChange}
                      required
                      placeholder="Tell us about your requirements or questions..."
                      className="textarea-premium"
                      disabled={isSubmitting}
                    ></textarea>
                  </div>

                  <button 
                    type="submit" 
                    className="submit-btn-premium"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="spinner-icon" />
                        Sending Your Message...
                      </>
                    ) : (
                      <>
                        Send Message <FaPaperPlane className="btn-icon-premium" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Review Submission Section */}
      <section className="review-section-premium">
        <div className="container-premium">
          <div className="section-header-premium">
            <motion.span 
              className="section-badge gold-badge"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <FaTrophy /> Share Your Experience
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Submit Your <span className="gradient-text-gold">Review</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Help us serve you better by sharing your feedback
            </motion.p>
          </div>

          {/* Review Stats Display */}
          {reviewStats && (
            <motion.div 
              className="review-stats-premium"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="stats-grid">
                <div className="stat-item">
                  <span className="stat-number">{reviewStats.total_reviews || 0}</span>
                  <span className="stat-label">Total Reviews</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">{reviewStats.average_rating ? reviewStats.average_rating.toFixed(1) : '0'}</span>
                  <span className="stat-label">Average Rating</span>
                  <div className="mini-stars">
                    {[...Array(5)].map((_, i) => (
                      <FaStar 
                        key={i} 
                        style={{ 
                          color: i < Math.round(reviewStats.average_rating || 0) ? '#FFD700' : '#e2e8f0',
                          fontSize: '14px'
                        }} 
                      />
                    ))}
                  </div>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <span className="stat-number">{reviewStats.five_star || 0}</span>
                  <span className="stat-label">5-Star Reviews</span>
                </div>
              </div>
            </motion.div>
          )}

          <div className="map-review-grid-premium">
            {/* Map Column */}
            <motion.div 
              className="map-column-premium"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="map-wrapper-premium">
                <div className="map-overlay-premium"></div>
                <iframe
                  title="Terra Gold Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30466.93782772085!2d74.10625814999999!3d17.346053599999998!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc1824f3b8d908f%3A0xdc673b1319576184!2sTalbid%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1781605781891!5m2!1sen!2sin"
                  height="100%"
                  width="100%" 
                  style={{ border: 0, borderRadius: '20px', minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="TerraGold Location Map"
                ></iframe>
                <div className="map-address-premium">
                  <FaMapMarkerAlt className="map-pin" />
                  <div>
                    <h4>TerraGold Facility</h4>
                    <p>A-54 MIDC Karad, Tal-Karad, Dist-Satara, 4115110</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Review Form Column */}
            <motion.div 
              className="review-column-premium"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="review-form-wrapper-premium">
                {reviewSubmitMessage && (
                  <div className={`message-banner ${reviewSubmitMessageType}`}>
                    {reviewSubmitMessageType === 'success' ? (
                      <FaCheckCircle className="message-icon" />
                    ) : reviewSubmitMessageType === 'error' ? (
                      <FaTimesCircle className="message-icon" />
                    ) : (
                      <FaExclamationTriangle className="message-icon" />
                    )}
                    <span>{reviewSubmitMessage}</span>
                  </div>
                )}

                <form onSubmit={handleReviewSubmit} className="review-form-premium">
                  {/* Rating Section */}
                  <div className="rating-section-premium">
                    <label>How would you rate your experience? *</label>
                    <div className="rating-stars-container">
                      {renderStars(reviewData.rating, true)}
                    </div>
                    <span className="rating-label">
                      {getRatingLabel(reviewData.rating)}
                    </span>
                  </div>

                  <div className="review-form-grid">
                    <div className="form-group-premium">
                      <label>
                        <FaUserCircle className="input-icon-premium" />
                        Your Name *
                      </label>
                      <input
                        type="text"
                        name="customerName"
                        value={reviewData.customerName}
                        onChange={handleReviewChange}
                        required
                        placeholder="Enter your full name"
                        className="input-premium"
                        disabled={isReviewSubmitting}
                      />
                    </div>

                    <div className="form-group-premium">
                      <label>
                        <FaEnvelope className="input-icon-premium" />
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="customerEmail"
                        value={reviewData.customerEmail}
                        onChange={handleReviewChange}
                        required
                        placeholder="your@email.com"
                        className="input-premium"
                        disabled={isReviewSubmitting}
                      />
                    </div>
                  </div>

                  <div className="form-group-premium">
                    <label>
                      <FaLeaf className="input-icon-premium" />
                      Product Name <span className="optional-label">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      name="productName"
                      value={reviewData.productName}
                      onChange={handleReviewChange}
                      placeholder="e.g., Organic Turmeric Powder, Pure Honey, etc."
                      className="input-premium"
                      disabled={isReviewSubmitting}
                    />
                  </div>

                  <div className="form-group-premium">
                    <label>
                      <FaComment className="input-icon-premium" />
                      Your Review *
                    </label>
                    <textarea
                      name="reviewText"
                      rows="3"
                      value={reviewData.reviewText}
                      onChange={handleReviewChange}
                      required
                      placeholder="Share your experience with TerraGold products..."
                      className="textarea-premium"
                      disabled={isReviewSubmitting}
                    ></textarea>
                  </div>

                  {/* Image Upload Section */}
                  <div className="image-upload-section-premium">
                    <label>
                      <FaCamera className="input-icon-premium" />
                      Upload Product Image <span className="optional-label">(Optional)</span>
                    </label>
                    <div className="image-upload-wrapper">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="image-upload-input"
                        id="review-image"
                        disabled={isReviewSubmitting}
                      />
                      <label htmlFor="review-image" className="image-upload-label">
                        {imagePreview ? (
                          <div className="image-preview-container">
                            <img src={imagePreview} alt="Product preview" className="image-preview" />
                            <span className="change-image-text">Change Image</span>
                          </div>
                        ) : (
                          <div className="upload-placeholder">
                            <FaUpload className="upload-icon" />
                            <span>Click or drag to upload image</span>
                            <span className="upload-subtext">PNG, JPG up to 5MB</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="submit-btn-premium review-submit-btn"
                    disabled={isReviewSubmitting}
                  >
                    {isReviewSubmitting ? (
                      <>
                        <FaSpinner className="spinner-icon" />
                        Submitting Your Review...
                      </>
                    ) : (
                      <>
                        Submit Review <FaStar className="btn-icon-premium" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Connect Section */}
      <section className="social-connect-premium">
        <div className="container-premium">
          <div className="section-header-premium">
            <motion.span 
              className="section-badge"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <FaUsers /> Connect With Us
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              Follow <span className="gradient-text-gold">TerraGold</span> on Social Media
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Stay updated with our latest products, wellness tips, and special offers
            </motion.p>
          </div>

          <div className="social-grid-premium">
            {socialLinks.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="social-card-premium"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ 
                  y: -10,
                  boxShadow: `0 20px 40px ${social.color}30`
                }}
                style={{ '--social-color': social.color }}
              >
                <div className="social-icon-premium" style={{ background: social.color }}>
                  {social.icon}
                </div>
                <span>{social.name}</span>
                <FaArrowRight className="social-arrow" />
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contactus;